/**
 * Compose, bake, persist — and the three dependency lists that must never drift apart.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-8 / AUD-2 RC-4) with no change to a single body.
 * AUD-2 asked for `appearanceDeps`, the bake-dirty effect, the imperative bake handle and the
 * debounced live-preview effect to live in ONE module, because the bug class is them
 * DISAGREEING: an input that recomposes the preview but never re-dirties the bake is a Save that
 * silently skips the upload — the stale-cross-device-assets report, twice shipped.
 *
 * What this hook owns:
 *  - `composeMockupOnce` (exported, so J-15/J-16/J-17 can drive the pipeline without the 320 ms
 *    debounce) and `previewMockup`, its ED-5 serialized entry point;
 *  - `appearanceDeps` — the SINGLE list the dirty effect and the preview effect both spread —
 *    and `backdropPreviewDeps`, which P3-1 appends to the PREVIEW list and only there;
 *  - `markBakeDirty` and the A3 bake-dirty effect, with ED-5's `dirtyEpochAtStart` conditional
 *    clean, F1's `restoreEpochRef` consume-once and `bakeDirtyMountRef`'s first-run skip;
 *  - the A3 imperative `MaskBakeHandle` TemplateEditor.saveDraft drives;
 *  - `aiStatus`, the operator-visible readout of which print-area source answered.
 *
 * `SHADING_TUNED` moved with it: the compose is its only reader.
 */
import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import type { EngineBinding, FitMode, MagentaMarkerResult, MockupAssets, Polygon, ShadingParams, VarnishParams } from "@genz/engine-bind";
import type { AppLogger } from "../app/context.js";
import type { EditorRefs } from "./editorContext.js";
import type { ComposeReason, MaskBakeHandle, MatteKind, MaskEditorProps } from "./maskEditorTypes.js";
import { dataUrlToBytes, decodePngToRgba, get2dContext, rgbaToPngBytes, solidOpaqueBytes } from "./pixels.js";
import { extractShell, printQuadFromGreen, productMaskFromAlpha, type ProductCategory } from "./compositing.js";
import { printQuadFromAlpha } from "./placement.js";
import { clearLayerForFrame } from "./clearCaseLayer.js";
import { CALIBRATION_DEFAULTS, resolveCalibration } from "../stores/segmenterCalibration.js";
import { putMockup, hasMockup } from "../stores/mockupStore.js";
import { removeGuideCast, type GuideKeyOptions } from "./chromaKey.js";
// WS-J J-17 / D-9: the pixel chain, off the main thread when the runtime allows it.
import { runComposePixels, type ComposePath } from "./composeWorkerClient.js";

/** The shipped depth of the case's recorded shadows and highlights over the artwork. Both are
 *  calibrated since 2026-10-12 (owner: the keyed print area lost its shadow and highlight
 *  definition): once for every customer in Admin → AI matte, and per template by an admin
 *  in the editor. These are the values when nothing was set. */
export const SHADOW_DEPTH_DEFAULT = CALIBRATION_DEFAULTS.key_shadow_depth;
export const HIGHLIGHT_DEPTH_DEFAULT = CALIBRATION_DEFAULTS.key_highlight_depth;

/** Artwork-shading engine calibration (everything EXCEPT the seven user sliders — strength,
 *  highlights, gradient, grain, sheen, shadow depth, highlight depth). Values fixed by the shading_lab A/B study against the owner's reference
 *  composite (2026-07-31): gain 3 + lowpass frequency separation transfer the case's subtle
 *  vignette without exploding grain, and linear-light keeps saturated designs from
 *  over-darkening. */
export const SHADING_TUNED = {
  // `min` is the floor on the LINEAR-light factor, so what the eye sees is its square root:
  // the darkest a shaded pixel can get is √min × the design colour. The old floor 0.02 = √
  // 0.141, i.e. 14% of the fill — on a flat saturated #FFEE00 that is RGB(36,34,0), which
  // reads as black, and the owner reported exactly that ring around the camera bump.
  // 0.20 = √0.447 floors it at RGB(114,106,0), a deep mustard that still reads as the design
  // colour. Measured in the camera-well ring (shading_lab, production sliders s .6 / grad .42
  // / grain .55 / sheen 1.2): flat-yellow darkest observed ratio 0.115 → 0.422, share of well
  // pixels below 0.40 1.45% → 0.00%, while the p25/p50 of the well ring move < 0.02 and the
  // leopard reference composite is visually unchanged (its well p5 0.573 → 0.617). The floor
  // only ever binds on the ~0.02% of print pixels the gain drives past it, so this buys the
  // anti-crush guarantee at almost no cost in depth.
  gain: 3, detailGain: 1.5, min: 0.2, max: 2.0, lowpass: 0.02, linear: true,
  // Asymmetric camera-depth: shadows deepened 1.5× (A/B'd 1.3/1.6/2.0 vs the reference, then
  // re-measured 1.4/1.45/1.5/1.6 after the "too dark" report). 1.6 kept the lens-ring depth
  // but sat right on the floor; 1.35 and below cost real depth on the reference (leopard well
  // p5 0.573 → 0.660). 1.5 gives back ~11% at the well's 5th percentile (0.531 → 0.591) for a
  // 7% shallower reference tail — the smallest trim that stops the ring from riding the clamp.
  shadowBoost: SHADOW_DEPTH_DEFAULT,
  // Gloss: lit-side deviations amplified 1.4× (bump-edge rims, top ramp); max raised to 2.0
  // so the rims have headroom. Calibrated with the sheen against the reference's glare.
  highlightBoost: HIGHLIGHT_DEPTH_DEFAULT,
} as const;

/**
 * The feather of the print window. A print guide key ends at a hard pixel edge and records an
 * edge feather of 0, so it never took the feather an AI matte gets: the design met the case
 * rim in stair-steps (owner, 2026-10-13). It takes the platform's edge feather, and 1 px at
 * the least.
 */
export function printWindowFeather(kind: string | null | undefined, edgeFeather: number): number {
  return kind === "chroma" ? Math.max(1, resolveCalibration().edge_feather_px) : edgeFeather;
}

/** The operator-visible readout of the last compose (J-12 names the shape so the control card
 *  that renders it can take it as a prop). Fields and values are unchanged. */
export interface AiStatus {
  source: "marker" | "legacy";
  printPct: number;
  mattePct: number;
  aiEdge: "on" | "skipped" | "off";
  hasDesign: boolean;
}

export interface UseMockupBakeArgs {
  /** The print guide key's options: a move must redraw the picture and re-key the bake. */
  guideKey?: GuideKeyOptions;
  engine: EngineBinding;
  shadedMagentaGuide?: boolean;
  locked?: boolean;
  logger?: AppLogger;
  versionId?: string;
  view?: string;
  onBaked?: MaskEditorProps["onBaked"];
  bakeRef?: Ref<MaskBakeHandle>;
  /** The nine shared refs (J-3) — ED-5's epoch, ED-6's upload window, F1's restore epoch. */
  editorRefs: EditorRefs;

  // --- source (J-6) ---
  chromaReady: boolean;
  readSource: () => { data: Uint8ClampedArray; width: number; height: number } | null;
  originalBytesRef: React.RefObject<Uint8Array | null>;
  caseImageFailedRef: React.RefObject<boolean>;

  // --- matte (J-7) ---
  markerRef: React.RefObject<MagentaMarkerResult | null>;
  matteKindRef: React.RefObject<MatteKind | null>;
  matteClearedRef: React.RefObject<boolean>;
  printAlphaFor: (
    src: { data: Uint8ClampedArray; width: number; height: number },
    marker: MagentaMarkerResult | null,
  ) => Promise<{ alpha: Uint8Array; from: string } | null>;
  reapplyMatteRef: React.RefObject<() => boolean>;
  removed: boolean;

  // --- layer bytes + the display URL (J-9 takes these over) ---
  testDesignRef: React.RefObject<Uint8Array | null>;
  clearBgRef: React.RefObject<Uint8Array | null>;
  /** Generate's clear image when it is the clear-case layer: it shares the case frame. */
  alignedClearRef: React.RefObject<Uint8Array | null>;
  textureRef: React.RefObject<Uint8Array | null>;
  clearBgUrlRef: React.RefObject<string | null>;
  textureUrlRef: React.RefObject<string | null>;
  bgImageRef: React.RefObject<HTMLImageElement | null>;
  /** ED-7 — `setDisplayImage` is owned by `useLayerAssets` (J-9), which is called after this
   *  hook; the compose only ever repaints from an async callback, so the late-bound ref is safe. */
  setDisplayImageRef: React.RefObject<(url: string | undefined, isObjectUrl?: boolean) => void>;
  /** The component owns this ref (`useMatte` reads it), so the hook only ASSIGNS `.current`. */
  previewMockupRef: React.RefObject<(uploadBake?: boolean, reason?: ComposeReason) => Promise<boolean>>;
  setPlacementMsg: (m: string | null) => void;

  // --- geometry ---
  printArea: Polygon;

  // --- every input in `appearanceDeps` ---
  markerColor: [number, number, number] | null;
  markerTol: number;
  textureBlend: string;
  varnish: VarnishParams;
  designRotation: number;
  layerOpacity: { phone: number; design: number; texture: number };
  profileCategory: ProductCategory;
  profileClear: boolean;
  profileTexture: boolean;
  aiEdge: boolean;
  aiEdgeKeep: number;
  fillFit: FitMode;
  designAspect: [number, number] | null;
  edgeChoke: number;
  edgeFeather: number;
  matteStrength: number;
  stageDespill: boolean;
  stageRefine: boolean;
  stageMatteStrength: boolean;
  stageShading: boolean;
  printShadingStrength: number;
  shadeGradient: number;
  shadeGrain: number;
  shadeSheen: number;
  shadeHighlights: number;
  shadeShadowDepth: number;
  shadeHighlightDepth: number;
  shadeInkBlack: number;
  shadeInkWhite: number;
  matteQuality: "fast" | "quality";
  protectProduct: boolean;

  // --- `backdropPreviewDeps` (P3-1: preview only, never the dirty set) ---
  bgMode: string;
  bgColor: string;
  blendMode: GlobalCompositeOperation;
  /** WS-BG F2 — the TEMPLATE's own background colour (`render_settings.background_color`,
   *  already resolved and format-adjusted by the caller). The compositor starts the canvas from
   *  it, so the preview on screen shows the background the EXPORT will paint instead of white.
   *
   *  Preview-only in the same sense as the backdrop above: it is a RENDER SETTING, not a baked
   *  asset, so it recomposes the preview and must never mark the bake dirty. `undefined`/`null`
   *  ⇒ the historical white canvas. */
  templateBackgroundColor?: [number, number, number, number] | null;
}

export function useMockupBake(args: UseMockupBakeArgs) {
  const shadedMagentaGuide = args.shadedMagentaGuide ?? false;
  const {
    engine, locked, logger, versionId, view, onBaked, bakeRef, editorRefs,
    chromaReady, readSource, originalBytesRef, caseImageFailedRef,
    markerRef, matteKindRef, matteClearedRef, printAlphaFor, reapplyMatteRef, removed,
    testDesignRef, clearBgRef, alignedClearRef, textureRef, clearBgUrlRef, textureUrlRef, bgImageRef,
    setDisplayImageRef, previewMockupRef, setPlacementMsg,
    printArea,
    markerColor, markerTol, textureBlend, varnish, designRotation, layerOpacity,
    profileCategory, profileClear, profileTexture, aiEdge, aiEdgeKeep, fillFit, designAspect,
    edgeChoke, edgeFeather, matteStrength, stageDespill, stageRefine, stageMatteStrength,
    stageShading, printShadingStrength, shadeGradient, shadeGrain, shadeSheen, shadeHighlights,
    shadeShadowDepth, shadeHighlightDepth, shadeInkBlack, shadeInkWhite,
    matteQuality, protectProduct, bgMode, bgColor, blendMode, templateBackgroundColor,
    guideKey,
  } = args;

  /** Live status of the last mockup preview so the operator can SEE what's happening
   *  (which print-area source, its coverage, the matte coverage) instead of guessing when
   *  it "goes grey". Set by previewMockup; shown under the preview. */
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
  // J-13: stable identity so the bake-dirty effect below can list it honestly. Body unchanged.
  const markBakeDirty = useCallback(() => { editorRefs.bakeDirtyRef.current = true; editorRefs.bakeDirtyEpochRef.current += 1; }, [editorRefs]);
  /** Whether this engine build can compose at all — a boolean, so the preview effect's dep list
   *  cannot be restarted by a parent handing down a fresh binding object. */
  const canCompose = !!engine.composeMockup;
  const consumedRestoreEpochRef = useRef(0);

  /** The clear-case (`behind`) layer. Generate's clear image is cropped to the product box so the
   *  engine's cover-fit places it 1:1 under the print area; any other image stays as picked. */
  const clearCaseLayer = async (w: number, h: number, productMask: Uint8Array, printMask?: Uint8Array) => {
    const bytes = profileCategory === "phone_case" && profileClear ? clearBgRef.current : null;
    if (!bytes || bytes !== alignedClearRef.current) return bytes;
    return clearLayerForFrame(bytes, productMask, w, h, printMask);
  };

  /**
   * Spec 019: composite the layered mockup via the engine (`composeMockup`) and show it
   * as the displayed image — proves the bake → engine → preview path end to end. Uses the
   * green-removed shell over the product silhouette (classical product mask), ground-
   * multiply preserving the shadow. Feature-detected (needs the engine binding + a real
   * canvas), so it no-ops in tests / web-without-engine.
   */
  /**
   * WS-ERR F-7 — failures THIS EDITOR MOUNT has already reported, keyed by kind.
   *
   * The refusals and the local-persist failure logged unconditionally, and the LIVE pass fires
   * on every slider notch behind a 320 ms debounce and on mount. A case image that cannot be
   * read, or a full disk, therefore wrote one line every third of a second for as long as the
   * editor was open — the amplifier `docs/release-audit-checklist.md` names ("A recurring
   * failure logs ONCE"), burying the one line that matters under thousands that do not. Same
   * shape as `queue/manager.ts`'s `persistFailed`, keyed so one failure cannot silence another.
   *
   * SCOPE, precisely: a `useRef`, so the latch is per MOUNT — not per process and not per
   * template. Closing the editor and reopening it reports each kind once more, which is what
   * an operator who has just changed something expects, and a view switch is a prop change
   * rather than a remount so it does NOT reset it. "Once per session" would be a module-level
   * Set, and this is deliberately not that: a fresh mount is a fresh question.
   */
  const reportedRef = useRef<Set<string>>(new Set());

  const composeMockupOnce = async (requestedUploadBake = true, reason: ComposeReason = "operator"): Promise<boolean> => {
    if (locked || !engine.composeMockup) return false;
    /* A compose the operator ASKED for must never just… not happen. Every dead end below
       returns `false`; the ones that used to do it in silence read as a button that does
       nothing, because the status line kept whatever was there before. Declared HERE, at the
       top, rather than two hundred lines in — three earlier dead ends (no readable source
       canvas, a marker despill/pick that failed, a tainted canvas on the legacy path) sat
       above the old declaration and were the silent ones.

       Only a compose the operator ASKED for speaks — see `ComposeReason`. The debounced live
       pass and the bake that persists a finished removal both own no status line. */
    const sayWhyNot = (msg: string) => { if (reason === "operator") setPlacementMsg(msg); };
    /* F-7 — the log half of the same rule. An OPERATOR press is user-paced: one press, one
       line, always. Every other reason (the debounced live pass, the persist bake) reports a
       given failure ONCE per session and is then quiet about it. `key` is the failure's kind,
       so a full disk cannot silence an unreadable canvas. */
    const sayInLog = (key: string, level: "warn" | "error", message: string, ctx?: Record<string, unknown>) => {
      if (reason !== "operator") {
        if (reportedRef.current.has(key)) return;
        reportedRef.current.add(key);
      }
      logger?.log(level, message, ctx);
    };
    // ED-6 INVARIANT: never let a cloud write leave the layer-upload window. While a custom
    // layer's hosting PUT is in flight `textureUrlRef`/`clearBgUrlRef` are still null, and the
    // cloud persist is a FULL OVERWRITE — so emitting `layerRefs` now would ERASE the
    // previously-saved texture/clear-bg url, with the corrective re-bake only arriving after
    // the version locked (409, unrecoverable). Downgrade to a LIVE compose: the display still
    // updates, and the cloud write happens in the re-bake fired once the url resolves (or in
    // `bakeNow`, which awaits the upload first). Guards EVERY caller, not just the picker.
    const uploadBake = requestedUploadBake && editorRefs.layerUploadRef.current === null;
    // True once the upload-bake block hands a payload to `onBaked` — bakeNow reads it.
    let emitted = false;
    // A3 dirtiness: a LIVE re-compose (uploadBake=false, fired by the debounced appearance
    // effect) reflects a change that has NOT been uploaded — mark the bake state dirty so a
    // following Save forces an upload-bake. Cleared only when an upload-bake emits `onBaked`
    // below. (An upload-bake here is the thing that CLEANS the state, so it must not self-dirty.)
    if (!uploadBake) markBakeDirty();
    // ED-5: the dirty EPOCH as this compose starts. A slider moved WHILE this bake runs bumps the
    // epoch; the emit below then refuses to clear `bakeDirtyRef`, because the assets it uploaded
    // predate that change. (The old code cleared the flag unconditionally, so the later change was
    // never uploaded — it just vanished.)
    const dirtyEpochAtStart = editorRefs.bakeDirtyEpochRef.current;
    const t0 = typeof performance !== "undefined" ? performance.now() : 0;
    let usedMarkerPath = false;
    let composePath: ComposePath = "fallback";
    let usedAiEdge = false;
    let aiEdgeSkipped = false;
    let printCoveragePct = 0;
    let matteCoveragePct = 0;
    let markerCoverage = 0;
    // The FULL shading calibration: the strength slider + the synthetic-light sliders on top of
    // the study-tuned engine constants. P2-1: this object is ALWAYS built and ALWAYS persisted,
    // even while shading is inactive. It used to be `undefined` whenever the stage was off or the
    // strength was 0, so `uploadBakedAssets` omitted the whole `shading` key — and the cloud
    // persist FULL-OVERWRITES, so a save made with shading off wiped the tuned gradient/grain/sheen
    // and turning shading back on came up at the defaults. The calibration is persisted
    // independently of whether it is currently ACTIVE; the effective RENDER value is zeroed below.
    const shadingCalibration: ShadingParams = {
      strength: printShadingStrength, ...SHADING_TUNED,
      gradient: shadeGradient, grain: shadeGrain, sheen: shadeSheen, highlights: shadeHighlights,
      shadowBoost: shadeShadowDepth, highlightBoost: shadeHighlightDepth,
      inkBlack: shadeInkBlack, inkWhite: shadeInkWhite,
      smoothing: resolveCalibration().shading_smoothing,
      caseHighlights: resolveCalibration().case_highlights,
    };
    // The EFFECTIVE value this compose (and the baked `MockupAssets`) render with. `undefined`
    // when the stage is off / strength 0 — the engine then reads the legacy scalar (also 0) → flat.
    // Old engine builds ignore the object and fall back to the legacy scalar we still pass.
    const effectiveShading: ShadingParams | undefined =
      stageShading && printShadingStrength > 0 ? shadingCalibration : undefined;
    // What is STORED carries the platform count it was saved at (see `templateLight`).
    const stamp = { lightRev: resolveCalibration().light_revision };
    const src = readSource();
    const canvas = editorRefs.sourceRef.current;
    if (!src || !canvas || typeof canvas.toDataURL !== "function") {
      sayInLog("source-unreadable", "warn", "Editor · Mockup preview skipped — the case image could not be read back from the canvas", {
        step: "composeMockup", hasSource: !!src, hasCanvas: !!canvas,
      });
      sayWhyNot("Couldn't read this case image — reload the template, or replace the reference image.");
      return false;
    }
    // The product mask comes from the CURRENT source alpha — i.e. the AI auto-clean mask the
    // user already applied (no classical re-segmentation, spec 022). Run "Auto-clean (AI
    // background)" first so the object is isolated; otherwise the mask covers the whole frame.
    //
    // J-17: computed by whichever branch runs. The marker path gets it back from `composePixels`,
    // which needs it anyway for the shell alpha — computing it twice would put back a whole-image
    // pass J-16 had just removed. `src.data` is also what the worker takes by TRANSFER, so it must
    // not be read after that point on the marker path (it is not).
    let productMask: Uint8Array;
    // WS-J / F-38 (J-16): `generated` is encoded by the branch that KEEPS it. This used to be a
    // full-frame PNG encode of the source canvas right here — and the marker branch below then
    // threw it away, ~33 lines later, replacing it with `marker.despilledPng`. At 2048² that was a
    // wasted encode on every compose, the debounced live preview included (it fires on every
    // slider move). Upload bake 6 → 5 encodes; live preview 3 → 2, or 1 with a transparent
    // backdrop. 2 is the FLOOR while `ComposeMockupArgs` takes PNG bytes; widening that signature
    // is WS-H2's file, not this one.
    let generated: Uint8Array;

    // APPROVED FLOW: build the shell + print mask from the synthetic MAGENTA marker. The
    // keyer returns a despilled product (magenta neutralized) + the print region; the shell
    // is that despilled product with the OUTER background and the PRINT hole knocked out
    // (alpha = productMask × (1 − printMask)) so the design shows through the print area and
    // the clear frame composites on top. Falls back to the legacy green path when no marker.
    let marker = markerRef.current;
    if (!marker && originalBytesRef.current && engine.detectMagentaMarker) {
      try {
        marker = shadedMagentaGuide && !markerColor && engine.detectShadedMagentaMarker
          ? await engine.detectShadedMagentaMarker(originalBytesRef.current)
          : await engine.detectMagentaMarker(originalBytesRef.current, 0, markerColor, markerTol);
        markerRef.current = marker;
      } catch { /* best-effort */ }
    }
    let shellPng: Uint8Array | null = null;
    let printMask: Uint8Array | undefined;
    let quad: Polygon;
    if (marker && marker.quad && marker.coverage >= 0.01) {
      usedMarkerPath = true;
      markerCoverage = marker.coverage;
      const despilled = await decodePngToRgba(marker.despilledPng, src.width, src.height);
      // WS-I / D-11 (A5): the print mask now comes from the SAME keyer as the preview matte on
      // the AUTO magenta path, so the cutout the operator approved is the cutout that bakes.
      // The despill still comes from the engine (luma-preserving, better than the key's
      // unpremultiply-to-white at a = 0) — WS-I moved the mask, not a single despilled byte.
      const picked = await printAlphaFor(src, marker);
      if (!despilled || !picked) {
        sayInLog("marker-empty", "warn", "Editor · Mockup preview skipped — the magenta marker despill/print-alpha step produced nothing", {
          step: "composeMockup", despilled: !!despilled, printAlpha: !!picked,
        });
        sayWhyNot("Couldn't read the print guide on this image — re-run “Print guide key (no AI)”, or draw the 4 corners with “Seed rectangle”.");
        return false;
      }
      const printAlpha = picked.alpha;
      logger?.log("info", `Editor · print mask from ${picked.from}`);
      // The case colours of the picture come from this despilled copy, so the key's colour
      // removal has to reach it too, or its slider changes nothing on screen.
      removeGuideCast(despilled, printAlpha, src.width, src.height, guideKey?.colour ?? 0);
      // Use the DESPILLED product (magenta neutralized) as `generated` too, so the
      // ground-multiply (step 2) never multiplies RAW magenta over the background at the
      // anti-aliased silhouette edge — no magenta/pink fringe leaks into the baked/exported
      // mockup. Matches the engine reference path (marker_compose passes the despilled image).
      generated = marker.despilledPng;
      // WS-J J-17 / D-9: the pixel chain — solidify → AI edge → feather → shell alpha → the two
      // coverage counts — now lives in `composePipeline.ts` and runs in a WORKER when the runtime
      // has one. AUD-2 IM-6 measured exactly this chain at 0.8–1.2 s for a live compose (the
      // 320 ms debounce behind every slider notch), all of it on the thread that also has to
      // paint. The fallback is the same function inline, so the bytes cannot differ — see
      // `compose-worker-client.test.ts`. Behaviour is byte-identical: same order, same ×2 gain
      // (`PRINT_SOLIDIFY_GAIN`, which the keyer-parity oracle also records), same ≥50 % AI-edge
      // safety guard, same `featherMask` call.
      //
      // `matteAlphaRef.current` is COPIED, never transferred: the editor retains it for the AI-edge
      // combine and for the uploaded `mattePng`, and detaching it would blank the persisted matte.
      const ma = editorRefs.matteAlphaRef.current;
      let pixels;
      try {
        const run = await runComposePixels({
          width: src.width,
          height: src.height,
          printAlpha,
          sourceRgba: src.data,
          despilled,
          matte: ma ? (ma instanceof Uint8Array ? ma : new Uint8Array(ma)) : null,
          // Colour to alpha is already soft at every edge; a feather on top would change it.
          feather: matteKindRef.current === "chroma" && guideKey?.method === "bulk" ? 0 : printWindowFeather(matteKindRef.current, edgeFeather),
          aiEdge: aiEdge && ma ? { keep: aiEdgeKeep } : null,
          keyedShell: matteKindRef.current === "chroma",
        }, logger);
        pixels = run.result;
        composePath = run.path;
      } catch (e) {
        // The worker took the planes by transfer, so this compose cannot be retried inline with
        // detached buffers. Refuse loudly; the client has already retired the worker, so the NEXT
        // compose takes the fallback with intact buffers.
        sayInLog("worker-failed", "error", `Editor · compose worker failed — ${e instanceof Error ? e.message : String(e)}; the next compose runs on the main thread`);
        return false;
      }
      productMask = pixels.productMask;
      const printSolid = pixels.printSolid;
      usedAiEdge = pixels.usedAiEdge;
      aiEdgeSkipped = pixels.aiEdgeSkipped;
      shellPng = rgbaToPngBytes(pixels.shellRgba, src.width, src.height);
      printMask = printSolid;
      // Coverage %s for the status readout (so "goes grey" is a visible, diagnosable number).
      printCoveragePct = pixels.printCoveragePct;
      matteCoveragePct = pixels.matteCoveragePct;
      // Prefer an admin-edited 4-corner print area; otherwise fit the quad to the largest
      // KEYED region (connected-component noise rejection) so a stray keyed pixel can't
      // drag a corner to a random location — fall back to the marker's oriented quad.
      // (Never slice the first 4 of an N-gon — those aren't the corners.)
      quad = printArea.length === 4
        ? printArea
        : (printQuadFromAlpha(printSolid, src.width, src.height) ?? marker.quad);
    } else {
      // Legacy green path (old templates): green-removed shell + alpha-subtraction mask. THIS is
      // the branch that needs the source canvas encoded, so this is where the encode happens.
      // `dataUrlToBytes`'s `atob` + `charCodeAt` loop stays as it is: a `fetch(dataUrl)` round trip
      // would be faster and is ASYNC, and converting a synchronous helper on the bake's critical
      // path to async to save ~40 ms is not worth re-ordering ED-5's compose chain.
      try {
        generated = dataUrlToBytes(canvas.toDataURL("image/png"));
      } catch (e) {
        // A tainted (cross-origin) or oversized canvas. This used to end the bake in silence,
        // and the template then published with whatever assets the cloud already held.
        sayInLog("canvas-encode", "error", "Editor · Mockup preview failed — the case canvas could not be encoded", {
          step: "composeMockup", error: e,
        });
        sayWhyNot("Couldn't encode this case image — replace the reference image and try again. Nothing was baked.");
        return false;
      }
      productMask = productMaskFromAlpha(src.data, src.width, src.height);
      const shellRgba = extractShell(src.data, src.width, src.height);
      shellPng = rgbaToPngBytes(shellRgba, src.width, src.height);
      const printMaskRaw = new Uint8Array(src.width * src.height);
      let printCount = 0;
      for (let i = 0; i < printMaskRaw.length; i++) {
        const v = Math.max(0, src.data[i * 4 + 3] - shellRgba[i * 4 + 3]);
        printMaskRaw[i] = v;
        if (v > 16) printCount++;
      }
      printMask = printCount > printMaskRaw.length * 0.003 ? printMaskRaw : undefined;
      // When the green-subtraction print mask is empty (old/opaque template, or the green
      // wasn't keyed), the design will fill the whole silhouette rather than a print window.
      // That's the intended fallback, but WARN so it's diagnosable (not a silent surprise).
      if (!printMask) {
        sayInLog(
          "legacy-print-mask-empty", "warn",
          `Editor · legacy print mask empty (${printCount}/${printMaskRaw.length} px keyed) — design will fill the whole silhouette`,
        );
      }
      // ED-13: an N-gon print area used to be SLICED to its first 4 points — which are not its
      // corners, so the bake warped the design into a shape the admin never drew. Refuse with a
      // message instead of baking a wrong placement. (The marker path above doesn't slice: it
      // re-fits the quad to the keyed region.)
      if (printArea.length > 4) {
        setPlacementMsg(
          `The print area has ${printArea.length} corners — the mockup bake needs exactly 4. ` +
            "Double-click the extra points to delete them, or press “Seed rectangle”.",
        );
        // F-7: pre-existing, and the same amplifier — the live pass fires on every slider
        // notch, and an N-gon print area is a state the operator can sit in for minutes.
        sayInLog("ngon", "warn", `Editor · Mockup bake refused — print area has ${printArea.length} corners (a 4-corner quad is required)`);
        return false;
      }
      quad = printArea.length === 4 ? printArea : (printQuadFromGreen(src.data, src.width, src.height) ?? printArea).slice(0, 4);
    }
    if (!shellPng) {
      sayInLog("no-shell", "warn", "Editor · Mockup preview skipped — no shell image produced");
      sayWhyNot("Couldn't build the mockup from this case image — reload the template, or replace the reference image.");
      return false;
    }
    if (quad.length < 4) {
      sayInLog(
        "no-quad", "warn",
        "Editor · Mockup preview skipped — no valid print-area quad (set the 4 corners in the editor)",
      );
      /* Name only the keyers this build can actually run. `aiAvailable` is a term in
         `chroma-remove`'s `disabled` expression, so on the web/wasm binding "Remove background
         (AI)" is a greyed-out button — sending the operator to it is worse than saying nothing. */
      sayWhyNot(
        engine.removeBackgroundAI
          ? "No print area yet — run “Print guide key (no AI)” or “Remove background (AI)” to key it from " +
              "the marker, or draw the 4 corners with “Seed rectangle”."
          : "No print area yet — run “Print guide key (no AI)” to key it from the marker, or draw the " +
              "4 corners with “Seed rectangle”.",
      );
      return false;
    }
    // Bake + persist the reusable assets so the batch render composites design-over-shell
    // for this version (spec 019). Per-render design/texture/phone are added at render time.
    // Runs when a versionId is set (editor: persist locally under it) OR when only `onBaked` is
    // present (wizard EMIT-ONLY mode: the version doesn't exist yet, so we skip the version-keyed
    // `putMockup` and just hand the payload up — the wizard uploads it after submit).
    const phone = await clearCaseLayer(src.width, src.height, productMask, printMask);
    if (versionId || onBaked) {
      const baked: MockupAssets = {
        generated, shell: shellPng, productMask, printMask, printQuad: quad,
        clearPhone: profileCategory === "phone_case" && profileClear,
        phone,
        texture: (profileTexture || profileCategory === "uv_case")
          ? (textureRef.current ?? (textureBlend === "uvvarnish" ? solidOpaqueBytes(src.width, src.height) : null))
          : null,
        opacity: layerOpacity,
        blendMode: { texture: textureBlend },
        varnish: textureBlend === "uvvarnish" ? varnish : undefined,
        designRotation: designRotation || undefined,
        fillFit,
        designAspect: designAspect ?? undefined,
        printShadingStrength: stageShading ? printShadingStrength : 0,
        shading: effectiveShading && { ...effectiveShading, ...stamp },
      };
      // Persist locally under the version id — but ONLY when there IS one (emit-only wizard mode
      // has no version yet, so there's nothing to key a local mockup on).
      // The local persist is what the batch render reads back. It used to be fire-and-forget
      // with no catch, so a quota/encrypt failure became an unhandled rejection while the editor
      // showed a finished preview and Save reported success — and the next launch rendered
      // design-only. Say it where the operator is looking, and log it with a step name.
      if (versionId) {
        void putMockup(versionId, baked, view).catch((e) => {
          sayInLog("put-mockup", "error", "Editor · the baked mock-up could not be stored on this device — batch renders will fall back to the cloud copy", {
            step: "putMockup", versionId, view, error: e,
          });
          sayWhyNot("The mock-up was built but could not be saved on this device. Renders will use the cloud copy; free some disk space and re-open the template.");
        });
      }
      // Hand the baked assets up so the parent can upload them for cross-device sync (spec 019).
      // SKIP on a LIVE tweak (uploadBake=false) — the R2 upload is the slow, network-bound part
      // and must not fire on every debounced slider move; the explicit "Preview mockup" button +
      // uploads still upload. Encode the product + print masks as grayscale PNGs to host alongside.
      if (uploadBake && onBaked) {
        // F-38 (J-16): ONE reused RGBA scratch for all three grayscale encodes instead of three
        // fresh full-frame buffers — −33.6 MB of transient allocation at 2048². Safe because
        // `rgbaToPngBytes` copies the bytes into the canvas via `putImageData` before returning, so
        // nothing holds a reference to the scratch after the call.
        //
        // The tail zero-fill is not defensive padding — it is what makes the reuse EXACT. The old
        // code allocated a fresh (zeroed) buffer per call, so a plane SHORTER than the frame left
        // transparent black past its end. A reused scratch would leave the PREVIOUS mask's bytes
        // there instead. Every real frame has `m.length === n` and the fill is skipped; the branch
        // exists so that "byte-for-byte what it produced before" has no asterisk.
        const grayScratch = new Uint8ClampedArray(src.width * src.height * 4);
        const grayPng = (m: Uint8Array | Uint8ClampedArray) => {
          const rgba = grayScratch;
          const n = src.width * src.height;
          for (let i = 0; i < m.length; i++) {
            const v = m[i]; rgba[i * 4] = v; rgba[i * 4 + 1] = v; rgba[i * 4 + 2] = v; rgba[i * 4 + 3] = 255;
          }
          if (m.length < n) rgba.fill(0, m.length * 4);
          return rgbaToPngBytes(rgba, src.width, src.height);
        };
        const maskPng = grayPng(productMask);
        const printMaskPng = printMask ? (grayPng(printMask) ?? undefined) : undefined;
        // Persist the RAW matte (when a removal produced one) + the AI-edge flags so reopening
        // the template restores the "background removed" state AND the calibration slider
        // (which reads the raw matte) — instead of reverting the AI-remove button to un-run.
        const mattePng = editorRefs.matteAlphaRef.current ? (grayPng(editorRefs.matteAlphaRef.current) ?? undefined) : undefined;
        if (maskPng) onBaked({
          assets: baked, maskPng, printMaskPng, mattePng,
          aiEdge: editorRefs.matteAlphaRef.current ? aiEdge : undefined,
          aiEdgeKeep: editorRefs.matteAlphaRef.current ? aiEdgeKeep : undefined,
          // Per-template calibration — emitted only when a removal produced a matte (the sliders
          // are shown/meaningful only then), mirroring the aiEdgeKeep condition. A CHROMA cutout
          // ran NONE of these stages, so what it records must not describe edge processing that
          // never happened: it writes ZERO rather than the hidden, untouched slider state. Two
          // consumers read those zeros back. (1) The reopen restore seeds the sliders from them
          // for BOTH kinds (see `runMatteRestore`), so a later AI removal in that session keys
          // with 0/0/0 instead of inheriting a phantom feather/choke. (2) An OLDER build without
          // the chroma branch replays the saved alpha through the AI stages — with zeros that
          // replay is the closest approximation of the key available to it (feather/choke would
          // only erode the panel the key resolved analytically).
          edgeFeather: editorRefs.matteAlphaRef.current ? (matteKindRef.current === "chroma" ? 0 : edgeFeather) : undefined,
          edgeChoke: editorRefs.matteAlphaRef.current ? (matteKindRef.current === "chroma" ? 0 : edgeChoke) : undefined,
          matteStrength: editorRefs.matteAlphaRef.current ? (matteKindRef.current === "chroma" ? 0 : matteStrength) : undefined,
          // WHICH technique produced the cutout, so the reopen restore re-keys a chroma template
          // instead of replaying the grey matte through the AI-matte stages. Emitted ONLY for
          // chroma: absent already MEANS "ai" on every consumer (`layerRefs.ts` maps a missing
          // key to undefined and the restore only branches on `=== "chroma"`), so writing
          // `matte_kind: "ai"` would add a new key to every AI template's payload for no
          // behavioural gain. Dropping it is also how a chroma → AI switch clears the saved
          // kind: `uploadBakedAssets` REBUILDS the matte fields on a fresh matte and
          // `mergeViewRefs` replaces the view's refs wholesale, so no stale "chroma" survives.
          matteKind: editorRefs.matteAlphaRef.current && matteKindRef.current === "chroma" ? "chroma" : undefined,
          // Signal a DELIBERATE clear so the uploader drops (not carries forward) any saved matte on
          // this matte-less bake. Only meaningful when there's no live matte (else the fresh matte wins).
          matteCleared: editorRefs.matteAlphaRef.current ? undefined : matteClearedRef.current || undefined,
          layerRefs: {
            textureUrl: textureUrlRef.current ?? undefined,
            clearBgUrl: clearBgUrlRef.current ?? undefined,
            // The ENABLE flag for the texture/effect layer, persisted in its own right. It used to
            // be inferred from `textureUrl` on reopen, so enabling the layer and configuring it
            // WITHOUT picking an image (UV varnish is generated from the design and needs none)
            // saved nothing that said the layer was on — reopen turned it back off and the next
            // bake dropped the blend/varnish with it. Emit what was actually baked (see the
            // `baked.texture` gate above), so a uv_case's implicit layer round-trips too.
            // Emit the STATE, not the derived on-ness. This used to be
            // `profileTexture || profileCategory === "uv_case"`, because at the time the
            // category was not persisted and a uv_case's implicit layer had nothing else to
            // round-trip from. The category IS persisted now (`product_category` below), and
            // every consumer re-derives the implicit on-ness from it — the bake gate, the
            // fill list and the checkbox itself, which reads on and is disabled for uv_case.
            // Emitting the derived value instead wrote `true` back into `profileTexture` on
            // reopen, so a uv_case whose layer the operator had explicitly turned off came
            // back on the moment its category was later changed to anything else.
            textureOn: profileTexture,
            textureBlend: textureBlend !== "normal" ? textureBlend : undefined,
            varnish: textureBlend === "uvvarnish" ? varnish : undefined,
            designRotation: designRotation || undefined,
            fillFit,
            designAspect: designAspect ?? undefined,
            printShadingStrength, stageDespill, stageRefine, stageMatteStrength, stageShading,
            matteQuality,
            // P2-1: the CALIBRATION, not the effective value. Persisting `effectiveShading` here
            // dropped the whole key whenever shading was off/0, so the cloud's full-overwrite
            // erased the tuned gradient/grain/sheen. The render value is zeroed on `assets` above.
            shading: { ...shadingCalibration, ...stamp },
            // P1-1 / P1-2 / P1-3 — keying + profile inputs that RESHAPE the baked shell and print
            // mask. They had no storage field, so a reopen re-keyed with the defaults and the next
            // dirty Save uploaded a WRONG shell + print mask over the good ones.
            markerColor: markerColor ?? undefined,
            markerTol,
            shadedMagentaGuide: shadedMagentaGuide || undefined,
            protectProduct,
            productCategory: profileCategory,
          },
        });
        // A3: this upload-bake handed a fresh payload up for cross-device sync — the persisted
        // (or about-to-persist) assets now match the visible state, so clear dirtiness and record
        // the emit so `bakeNow()` can confirm a payload was actually handed up.
        // ED-5: only CLEAN the dirty flag when nothing changed since this compose started —
        // otherwise a finishing bake would swallow a LATER edit's dirtiness.
        if (maskPng) {
          if (editorRefs.bakeDirtyEpochRef.current === dirtyEpochAtStart) editorRefs.bakeDirtyRef.current = false;
          emitted = true;
        }
      }
    }
    // Build the back→front fill stack the studio render will use: clear-case PHONE (behind
    // the design) → the TEST DESIGN (fills the print area) → TEXTURE/effect overlay. Each
    // carries its per-layer opacity so the preview matches the baked render. The chosen
    // background (the "add background" controls) composites behind everything.
    const fills: Uint8Array[] = [];
    const fillOpacity: number[] = [];
    const fillBlend: string[] = [];
    // UV varnish needs no texture image — synthesize full-print coverage when none is uploaded.
    const dTexture = textureRef.current
      ?? ((profileTexture || profileCategory === "uv_case") && textureBlend === "uvvarnish"
        ? solidOpaqueBytes(src.width, src.height) : null);
    const dDesign = testDesignRef.current;
    if (dDesign) { fills.push(dDesign); fillOpacity.push(layerOpacity.design); fillBlend.push("normal"); }
    if ((profileTexture || profileCategory === "uv_case") && dTexture) { fills.push(dTexture); fillOpacity.push(layerOpacity.texture); fillBlend.push(textureBlend); }
    // Clear-case PHONE goes in the dedicated `behind` layer (fills the whole silhouette,
    // shows through the clear shell), NOT the print-area fill stack.
    const behind = phone;
    let background: Uint8Array | null = null;
    if (bgMode !== "transparent" && typeof document !== "undefined") {
      const bgCanvas = document.createElement("canvas");
      bgCanvas.width = src.width; bgCanvas.height = src.height;
      const bgCtx = get2dContext(bgCanvas);
      if (bgCtx) {
        if (bgMode === "color") { bgCtx.fillStyle = bgColor; bgCtx.fillRect(0, 0, bgCanvas.width, bgCanvas.height); }
        else if (bgMode === "image" && bgImageRef.current) { try { bgCtx.drawImage(bgImageRef.current, 0, 0, bgCanvas.width, bgCanvas.height); } catch { /* ignore */ } }
        try { background = dataUrlToBytes(bgCanvas.toDataURL("image/png")); } catch { background = null; }
      }
    }
    try {
      const tCompose = typeof performance !== "undefined" ? performance.now() : 0;
      // WS-BG F2: `backgroundColor` is the TEMPLATE's own background (the canvas the compositor
      // starts from); `background` above is the preview BACKDROP image the editor's own controls
      // build. Two different inputs, and the preview needs both or the operator picks a colour
      // and sees white until they export.
      const out = await engine.composeMockup({ generated, shell: shellPng, productMask, printMask, printQuad: quad, fills, fillOpacity, fillBlend, background, backgroundColor: templateBackgroundColor ?? null, behind, designRotation, fillFit, designAspect, varnish: textureBlend === "uvvarnish" ? varnish : undefined, printShadingStrength: stageShading ? printShadingStrength : 0, shading: effectiveShading });
      const now = typeof performance !== "undefined" ? performance.now() : 0;
      // Diagnostics for the "goes grey / slow" reports: which keying path, coverage, whether a
      // design was in the fill stack, and the timing split (total vs the compose call itself).
      logger?.log(
        "info",
        `Editor · Mockup preview: path=${usedMarkerPath ? "marker" : "legacy"} aiEdge=${usedAiEdge} skipped=${aiEdgeSkipped} print=${printCoveragePct.toFixed(1)}% matte=${matteCoveragePct.toFixed(1)}% coverage=${(markerCoverage * 100).toFixed(1)}% fills=${fills.length} design=${dDesign ? "yes" : "NO"} shading=${stageShading ? printShadingStrength : "OFF(stage)"} total=${Math.round(now - t0)}ms compose=${Math.round(now - tCompose)}ms bake=${uploadBake ? "yes" : "live"} worker=${composePath === "worker" ? "yes" : "fallback"}`,
      );
      // Surface the status so the operator can SEE the print-area source + coverage.
      setAiStatus({
        source: usedMarkerPath ? "marker" : "legacy",
        printPct: printCoveragePct,
        mattePct: matteCoveragePct,
        aiEdge: usedAiEdge ? "on" : aiEdgeSkipped ? "skipped" : "off",
        hasDesign: !!dDesign,
      });
      // The skip warn badge derives from `aiStatus.aiEdge === "skipped"` (set just above), so
      // there's no separate skip flag to lift — a non-skipping compose clears it, a guard-tripping
      // one raises it, both via this single `setAiStatus`.
      if (out && typeof URL !== "undefined") {
        // Show the composited mockup directly. Do NOT bump removalVer — that would re-run
        // the background-recomposite effect and immediately overwrite displayUrl with the
        // plain cleaned source (clobbering the mockup). The mockup IS the final composite.
        // ED-7: tracked as an object URL so the NEXT compose revokes it (one full-res PNG per
        // compose otherwise leaks for the lifetime of the document).
        setDisplayImageRef.current(URL.createObjectURL(new Blob([out as BlobPart], { type: "image/png" })), true);
        // A mockup PREVIEW is not a background removal — do NOT set `removed` (that flips the
        // button to "Restore background" + reads as if the AI removed the background, which
        // confused users uploading a test design). Just show the preview + a neutral message.
        setPlacementMsg(
          aiEdgeSkipped
            ? "AI edge had no small content to protect — kept the marker print area."
            : usedAiEdge
              ? "Mockup preview updated · AI edge refined the print area."
              : "Mockup preview updated.",
        );
        logger?.log("info", `Editor · Mockup preview composited (engine, ${out.length} bytes)`);
      }
    } catch (e) {
      setPlacementMsg("Mockup preview failed.");
      sayInLog("compose-threw", "error", `Editor mockup preview (layered compose) failed — ${e instanceof Error ? (e.stack ?? e.message) : String(e)}`);
    }
    return emitted;
  };

  /**
   * ED-5 — SERIALIZED entry point for `composeMockupOnce`.
   *
   * Composing is a long async pipeline (marker detect → decode → engine compose → optional R2
   * upload) that reads the source canvas and the layer refs as it goes. Two overlapping calls
   * (a Save-forced bake + the 320ms live-preview debounce, a layer pick + a slider drag…) used
   * to interleave: they double-uploaded the same assets, and whichever finished LAST won the
   * `displayUrl`/status regardless of which was newer. Chaining every call behind the previous
   * one makes the compose order deterministic and the upload single. A rejected link is caught
   * so one failure can never poison the chain.
   */
  const previewMockup = (uploadBake = true, reason: ComposeReason = "operator"): Promise<boolean> => {
    const run = editorRefs.composeChainRef.current.then(
      () => composeMockupOnce(uploadBake, reason),
      () => composeMockupOnce(uploadBake, reason),
    );
    editorRefs.composeChainRef.current = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  };
  // Keep the imperative handle pointed at the CURRENT previewMockup closure (it captures live
  // state/refs, so a stale one would bake old values). The handle itself is stable (deps []).
  previewMockupRef.current = previewMockup;

  // SINGLE SOURCE OF TRUTH for the bake-relevant APPEARANCE inputs (marker colour/tolerance,
  // background, blend, layer opacity, product profile, AI edge, fill/aspect, calibration sliders).
  // BOTH the bake-dirty effect AND the live-preview effect below spread this into their dep arrays
  // (the live one appends the lifecycle flags). Keeping it ONE list is deliberate: the dirty effect's
  // deps used to be a hand-copied mirror of the preview effect's appearance deps, and any drift
  // silently reintroduced the stale-cross-device-assets bug (a change that recomposes live but never
  // re-dirties → Save skips the upload-bake). React allows a spread dep array as long as its length
  // is stable across renders — this list's length is fixed, so it is safe. Recomputed each render.
  const appearanceDeps = [
    // The print guide key's options reshape the print area and the case colours.
    JSON.stringify(guideKey ?? null),
    // `markerColor` is an OBJECT, so it must be compared by VALUE here exactly as the
    // restore chain below compares it (`JSON.stringify(refs.markerColor) !== …`). Listed by
    // reference, a fresh-but-equal object from a re-render counted as a change while an
    // in-place mutation counted as none — and the two lists disagreeing about what "changed"
    // is precisely the asymmetry that shipped the dirty-on-open and skipped-bake bugs.
    JSON.stringify(markerColor), markerTol, shadedMagentaGuide, textureBlend, JSON.stringify(varnish), designRotation,
    // `layerOpacity` rides into the baked assets WHOLE — `.phone` included — but only
    // `.design` and `.texture` were listed here, so moving the phone-opacity slider
    // recomposed the preview and left the bake clean: Save then skipped the upload-bake and
    // the new value never reached the cloud. (Its matching restore clause is in
    // `restoreChangesDirtyDep` below; the two must be added together or reopening a template
    // that carries the field would dirty it on open.)
    layerOpacity.design, layerOpacity.texture, layerOpacity.phone,
    profileClear, profileTexture, profileCategory,
    aiEdge, aiEdgeKeep, fillFit, designAspect,
    // Per-template calibration sliders (B3) — a change reshapes the baked matte/shell, so Save
    // must upload fresh assets after calibrating.
    edgeFeather, edgeChoke, matteStrength,
    // Artwork shading + per-stage pipeline toggles — all persisted per-template (baked_assets), so
    // a change must dirty the bake AND (via the live-preview effect below) re-run live.
    printShadingStrength, stageShading, stageDespill, stageRefine, stageMatteStrength,
    shadeGradient, shadeGrain,
    // GEOMETRY + KEYING inputs that shape the BAKED assets and were missing here (audit):
    //  • printArea → baked.printQuad and the print-mask clip. Dragging a corner used to
    //    leave the bake clean, so Save persisted the new masks while the cloud kept the OLD
    //    print_quad — every other device rendered the design in the wrong place.
    //  • protectProduct → feeds applyMatteToSource, i.e. the product mask and shell.
    //    Toggling it visibly did nothing and never re-keyed.
    //  • shadeSheen / shadeHighlights → part of the persisted shading calibration (see below,
    //    also restored now).
    //  • matteQuality → persisted in layerRefs.
    JSON.stringify(printArea), protectProduct, shadeSheen, shadeHighlights, matteQuality,
    shadeShadowDepth, shadeHighlightDepth, shadeInkBlack, shadeInkWhite,
  ] as const;

  // P3-1 — the PREVIEW BACKDROP (`bgMode`/`bgColor`/`blendMode`). It composites behind the mockup
  // for the on-screen preview ONLY: it is applied AFTER the `baked` MockupAssets are assembled and
  // is never part of them, so changing it cannot alter a single byte of the uploaded assets. It
  // used to sit in `appearanceDeps`, which meant flipping the swatch marked the bake DIRTY and the
  // next Save re-uploaded byte-identical generated/shell/mask PNGs (and rotated the version token)
  // for nothing. It stays in the LIVE-PREVIEW effect's deps below — the picker must still recompose
  // what's on screen — but is deliberately OUT of the bake-dirty set.
  //
  // WS-BG F2: the TEMPLATE's background colour joins this list for exactly the same reasons. It
  // must recompose the preview — picking "Solid color" red and seeing white until the export is
  // the defect — and it must NOT dirty the bake, because it is a render setting the version
  // payload carries, not a byte of the baked assets. Joined as a string so the signature stays a
  // flat scalar list (see `appearanceSig` below).
  const backdropPreviewDeps = [
    bgMode, bgColor, blendMode, (templateBackgroundColor ?? []).join(","),
  ] as const;

  /**
   * MN-11 / J-13 — the two lists as single VALUES.
   *
   * Two of the wave's eight suppressions existed only because `[...appearanceDeps]` is a spread of
   * a VARIABLE: `react-hooks/exhaustive-deps` cannot verify one statically, so both effects below
   * had to be silenced. Joining each list into one string makes both dep arrays literal and
   * checkable — and turns the "these lists must not drift" invariant into a value a test can
   * assert directly instead of a convention three comment blocks ask future authors to honour.
   *
   * Plain `join`, deliberately NOT `useMemo`: memoising on a spread dep array is the same
   * unverifiable construct one level down, and joining ~25 scalars per render is free next to the
   * compose it gates. `\u0001` separates fields so no value can forge a neighbour's boundary.
   */
  const appearanceSig = appearanceDeps.join("\u0001");
  const backdropSig = backdropPreviewDeps.join("\u0001");

  // A3 bake dirtiness — mark dirty whenever a bake-relevant APPEARANCE input changes, even before
  // any live compose has rendered (e.g. the admin toggles AI edge / rotates the design before a
  // test design is loaded, so the debounced preview effect below is still a no-op). Consumes the
  // SHARED `appearanceDeps` list, MINUS the lifecycle flags (chromaReady / removed / locked) the
  // live-preview effect adds: those fire on load / on a removal (which auto-bakes and clears
  // dirtiness) and must not re-dirty it. Skips the initial mount so a freshly-opened, unedited
  // template starts CLEAN. Cleared only by a successful upload-bake (see previewMockup's emit).
  // Over-approximates safely — never false while a real pending change exists.
  const bakeDirtyMountRef = useRef(true);
  /**
   * F-35 / J-14 — a view switch used to REMOUNT this hook, which is how the bake-dirty state got
   * back to its mount values. Without a remount we have to say it: a freshly-switched view is as
   * clean as a freshly-opened one, and its first appearance-dep change (the new view's own masks
   * arriving as props) must be skipped exactly as the mount's was.
   */
  const seenBakeViewRef = useRef<string | undefined>(view);
  useEffect(() => {
    if (seenBakeViewRef.current === view) return;
    seenBakeViewRef.current = view;
    editorRefs.bakeDirtyRef.current = false;
    bakeDirtyMountRef.current = true;
  }, [view, editorRefs]);
  useEffect(() => {
    if (bakeDirtyMountRef.current) { bakeDirtyMountRef.current = false; return; }
    // F1: the reopen restore set these appearance deps to the SAVED values — that's not a user edit,
    // so it must not mark the (already-persisted) template dirty. The restore bumped restoreEpochRef;
    // consume it here exactly once and skip. A real change later sees epoch == consumed → dirties.
    // (All restore state sets land in one commit → one effect run → one consume; see restore effect.)
    if (editorRefs.restoreEpochRef.current !== consumedRestoreEpochRef.current) {
      consumedRestoreEpochRef.current = editorRefs.restoreEpochRef.current;
      return;
    }
    markBakeDirty();
    // J-13: `appearanceSig` IS the list (see above), so this array is literal and complete —
    // `markBakeDirty` and `editorRefs` are stable, `consumedRestoreEpochRef`/`bakeDirtyMountRef`
    // are refs.
  }, [appearanceSig, markBakeDirty, editorRefs]);

  // A3 imperative bake handle — lets TemplateEditor.saveDraft query dirtiness and force a
  // bake+upload before it submits the version.
  //
  // MN-11 / review-1 M-3: the list is no longer `[]`. Everything it reads through a ref is stable,
  // so those names cost nothing — but `versionId` and `view` are plain VALUES, and `hasBake()`
  // closes over both. `[]` went stale on a version change at base and, since F-35 removed the view
  // remount, on a view change too. Re-creating the handle when either changes is correct: it is
  // the only way `hasBake()` answers for the view the operator is actually looking at.
  useImperativeHandle(
    bakeRef,
    (): MaskBakeHandle => ({
      // ED-6: an OPEN custom-layer upload counts as dirty. Otherwise a Save inside that window
      // saw a clean editor, skipped `bakeNow`, and submitted refs whose texture/clear-bg url was
      // still unresolved — erasing the previously-saved one on the cloud's full overwrite.
      isDirty: () => editorRefs.bakeDirtyRef.current || editorRefs.layerUploadRef.current !== null,
      bakeNow: async () => {
        // ED-6: never bake inside the layer-upload window — wait for the hosted url to resolve so
        // the emitted `layerRefs` carry it. Bounded by the upload's own failure path (it always
        // settles), and a rejection is swallowed: a failed upload must not block the geometry save.
        const layerUpload = editorRefs.layerUploadRef.current;
        if (layerUpload) await layerUpload.catch(() => {});
        // A Save clicked INSIDE the live effect's 320ms debounce window races it: the source
        // canvas is still keyed with the OLD choke/feather/strength params (the debounced re-key
        // hasn't fired yet). Mirror the effect's sig-guard here so we bake pixels keyed with the
        // CURRENT params. matteParamsSigRef holds the live sig (the [] handle can't read the
        // slider state directly); appliedMatteParamsRef holds what's baked into the canvas.
        if (editorRefs.matteAlphaRef.current && editorRefs.appliedMatteParamsRef.current !== editorRefs.matteParamsSigRef.current) {
          reapplyMatteRef.current?.();
        }
        // previewMockup resolves true iff it emitted onBaked (a payload was handed up).
        return previewMockupRef.current(true);
      },
      hasBake: () => (versionId ? hasMockup(versionId, view) : false),
      reapplyMatte: () => reapplyMatteRef.current(),
      sourceReady: () => editorRefs.sourceRef.current !== null,
      caseImageFailed: () => caseImageFailedRef.current,
    }),
    [
      versionId, view,
      caseImageFailedRef, previewMockupRef, reapplyMatteRef,
      editorRefs.appliedMatteParamsRef, editorRefs.bakeDirtyRef, editorRefs.layerUploadRef,
      editorRefs.matteAlphaRef, editorRefs.matteParamsSigRef, editorRefs.sourceRef,
    ],
  );

  // LIVE preview: auto-recompose the mockup (debounced) whenever a keying/appearance input the
  // operator tweaks changes — marker colour/tolerance, background, blend, layer opacity, product
  // profile — so they no longer have to click "Preview mockup" for every change. Uses
  // uploadBake=false so the slow R2 sync never fires on a slider move (the button + uploads still
  // sync). Only runs once there's something to show (a test design or a picked marker colour), so
  // it never auto-composites an empty (grey) print area.
  useEffect(() => {
    if (locked || !canCompose || !chromaReady) return;
    // Proceed when there's a composable mockup to show: a test design, a picked marker colour, OR a
    // DETECTED marker (B5). AI-edge/matte calibration happens on generated marker templates where the
    // marker keys automatically and no colour was ever picked — without `markerRef` in the gate,
    // dragging a slider there showed nothing until "Preview mockup" was clicked. `markerRef` is the
    // cached detection (previewMockup reuses it), so this doesn't trigger a detection storm.
    if (!testDesignRef.current && !markerColor && !markerRef.current) return;
    const id = setTimeout(() => {
      // If a matte-side slider (choke/feather/strength) moved, the source canvas is still keyed with
      // the OLD params — re-key it from pristine FIRST so previewMockup reads the freshly-calibrated
      // cutout, THEN compose. `appliedMatteParamsRef` skips this when only a bg/blend/layer control
      // changed, so we don't re-run the erode/feather morphology needlessly. Sequenced inside this
      // one debounce so there's never a double compose. (reapplyMatte no-ops without a removal.)
      // J-13: read the LIVE sig and the LIVE closures through the refs the file already keeps for
      // exactly this (`bakeNow` does the same, and for the same reason). Listing `matteParamsSig`,
      // `reapplyMatte` and `previewMockup` as deps instead would restart this 320 ms debounce on
      // every render, because all three are redefined every render — the honest dep list has to
      // name stable things, and these refs are both stable AND strictly more current than the
      // closures captured when the effect ran.
      const sig = editorRefs.matteParamsSigRef.current;
      if (removed && editorRefs.matteAlphaRef.current && editorRefs.appliedMatteParamsRef.current !== sig) reapplyMatteRef.current();
      void previewMockupRef.current(false, "live");
    }, 320);
    return () => clearTimeout(id);
    // previewMockup reads current state/refs; deps are the inputs that should re-trigger it. Consumes
    // the SHARED `appearanceDeps` (so it can never drift from the bake-dirty effect — see item above)
    // plus the lifecycle flags this effect additionally gates on. `appearanceDeps` already carries
    // aiEdge/aiEdgeKeep + fillFit/designAspect + the calibration sliders, so toggling AI edge, changing
    // the fill/aspect, or dragging a calibration slider all re-fire the debounced preview.
    // P3-1: `backdropSig` is appended HERE and only here — the backdrop must recompose the live
    // preview but must never dirty the bake (it is not part of the baked assets).
    // J-13: complete and literal. `canCompose` is a BOOLEAN derived from `engine.composeMockup`
    // rather than `engine` itself, so a parent that re-creates the binding object cannot restart
    // the debounce; the compose itself goes through `previewMockupRef`, which always has the
    // current binding.
  }, [
    appearanceSig, backdropSig, chromaReady, removed, locked, canCompose, markerColor,
    editorRefs, testDesignRef, markerRef, reapplyMatteRef, previewMockupRef,
  ]);

  return {
    composeMockupOnce,
    previewMockup,
    markBakeDirty,
    appearanceDeps,
    backdropPreviewDeps,
    aiStatus,
    setAiStatus,
  };
}
